import { useEffect, useRef, useState } from "react";
import { Alert, Button, Input } from "reactstrap";

import {
  BrieGPTChat,
  Instruction,
  useBrieGPTChats,
  useBrieGPTProjects,
} from "./useBrieGPTItems";
import GlobalInstructionsMessage from "./GlobalInstructionsMessage";
import { BrieGPTTabs } from "./BrieGPTSidebarElements/Types";
import { newToastEvent } from "../../../utils/ToastUtil";
import ManageItems from "../../ManageItems/ManageItems";
import { DrawerProps } from "../../Drawers/DrawerTypes";
import AppSpinner from "../../../elements/AppSpinner";
import useUpdateGPTChats from "./useUpdateGPTChats";
import AppDrawer from "../../Drawers/AppDrawer";
import StrUtils from "../../../utils/StrUtils";
import AppInput from "../../AppInput";

type ViewChat = {
  type: "view";
  chatID: string | null;
  viewID: string;
};

type RecordChat = {
  type: "record";
  chatID: string | null;
  recordID: string | undefined;
  moduleAPIName: string;
};

type Props = (ViewChat | RecordChat) &
  DrawerProps & {
    tab: BrieGPTTabs | undefined;
    projectID: string | null | undefined;
  };

export default function ChatSettingsDrawer(props: Props) {
  const open = props.open;
  const onClose = props.onClose;
  const type = props.type;
  const chatID = props.chatID;
  const gptItemsProps =
    type === "view"
      ? { type, viewID: props.viewID }
      : {
          type,
          recordID: props.recordID,
          moduleAPIName: props.moduleAPIName,
        };

  const { updateChat, updating } = useUpdateGPTChats(
    props.projectID,
    props.tab
  );
  const { chats } = useBrieGPTChats(gptItemsProps);
  const { projects } = useBrieGPTProjects({
    ...gptItemsProps,
    tab: props.tab || "Personal",
  });

  let isProjectChat = false;
  let projectInstructions: Instruction[];

  let chat = chats?.find((c) => c?.id === chatID);
  if (!chat) {
    isProjectChat = true;
    const project = projects?.find((p) =>
      p?.Chats?.map((c) => c?.id)?.includes(chatID!)
    );
    projectInstructions = project?.InstructionList || [];
    chat = project?.Chats?.find((c) => c?.id === chatID);
  }

  const [chatName, setChatName] = useState("");
  const [instructionList, setInstructionList] = useState<Instruction[]>([]);
  const [error, setError] = useState("");
  const instructionsRef = useRef<HTMLTextAreaElement>(null);

  const changesMade =
    chatName !== chat?.Name ||
    JSON.stringify(instructionList) !== JSON.stringify(chat?.InstructionList);

  useEffect(() => {
    setChatName(chat?.Name || "");
    setInstructionList(chat?.InstructionList || []);
  }, [chat?.Name, chat?.InstructionList]);

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      headerTitle={"Chat Settings"}
      headerOptions={
        <Button
          onClick={handleUpdateChat}
          color="primary"
          size="sm"
          className="tw:w-24"
          disabled={updating || !changesMade}
        >
          {updating ? <AppSpinner size="sm" color="white" /> : "Save"}
        </Button>
      }
      body={
        <>
          <h4>Chat Name</h4>
          <Input
            value={chatName}
            onChange={(e) => setChatName(e.target.value)}
            className="tw:mb-4"
          />
          <h4>Instructions</h4>
          <AppInput
            innerRef={instructionsRef}
            className="tw:mt-2"
            type="textarea"
            placeholder="Add Instructions"
            rows={3}
            onKeyDown={handleKeyDown}
          />
          {error && (
            <Alert color="danger" className="tw:my-4 tw:py-2">
              {error}
            </Alert>
          )}
          <div
            className={`tw:my-3 tw:flex ${
              isProjectChat ? "tw:justify-between" : "tw:justify-end"
            }`}
          >
            {isProjectChat && (
              <div>
                <div className="tw:flex tw:items-center tw:mt-1">
                  <div className="tw:bg-tertiary tw:w-5 tw:h-5 tw:rounded-lg tw:border tw:border-gray-200"></div>
                  <div className="tw:text-xs tw:pl-1 tw:font-medium">
                    - Project Instruction
                  </div>
                </div>
                <div className="tw:flex tw:items-center tw:my-1 ">
                  <div className="tw:bg-primary-light-bg tw:w-5 tw:h-5 tw:rounded-lg tw:border tw:border-gray-200"></div>
                  <div className="tw:text-xs tw:pl-1 tw:font-medium">
                    - Chat Instruction
                  </div>
                </div>
              </div>
            )}
            <div className="tw:flex tw:items-center">
              {isProjectChat && (
                <Button
                  size="sm"
                  className="tw:flex tw:items-center"
                  onClick={resetProjectInstructions}
                >
                  Reset
                  <i className="mdi mdi-restart tw:pl-1 tw:text-lg tw:leading-0" />
                </Button>
              )}
              <Button
                color="primary"
                size="sm"
                className="tw:flex tw:items-center"
                onClick={addInstruction}
              >
                Add
                <i className="mdi mdi-keyboard-return tw:pl-2 tw:text-lg" />
              </Button>
            </div>
          </div>
          <GlobalInstructionsMessage />
          <ManageItems
            itemKey={undefined}
            nameKey={"Instruction"}
            items={instructionList}
            onReorder={({ newOrder }) => handleReorder(newOrder)}
            title="Instruction"
            description="No instructions have been added."
            onDelete={({ id: instruction }) => {
              setInstructionList((prev) =>
                prev.filter((i) => i.id !== instruction)
              );
            }}
            useTooltip
            copyable
            getColor={(i) =>
              i?.Project ? "var(--tertiary)" : "var(--primary-light-bg)"
            }
          />
        </>
      }
    />
  );

  function handleReorder(newOrder: string[]) {
    const newInstructions = newOrder.map((id) => {
      const instruction = instructionList.find((i) => i.id === id)!;
      return instruction;
    });

    setInstructionList(newInstructions);
  }

  function resetProjectInstructions() {
    const chatInstructions = instructionList.filter((i) => !i.Project);

    setInstructionList([...(projectInstructions || []), ...chatInstructions]);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      if (e.shiftKey) {
        // Shift + Enter: Add a new line
        e.preventDefault();
        instructionsRef.current!.value += "\n";
      } else {
        // Enter: Submit the message
        e.preventDefault();
        addInstruction();
      }
    }
  }

  function addInstruction() {
    const instruction = instructionsRef.current?.value;

    if (!instruction) return;

    // Only add the instruction if it doesn't already exist
    if (instructionList.find((i) => i.Instruction === instruction)) {
      return setError("Instruction already exists.");
    }

    const newInstruction = {
      id: StrUtils.uuidv4(),
      Instruction: instruction,
    };

    setError("");
    setInstructionList((prev) => [...prev, newInstruction]);
    instructionsRef.current!.value = "";
  }

  function handleClose() {
    onClose();
  }

  async function handleUpdateChat() {
    if (!chatID || (props.type === "record" && !props.recordID)) {
      return newToastEvent("error", "No chat selected.");
    }

    const payload: Partial<BrieGPTChat> = {};

    if (chatName !== chat?.Name) {
      payload.Name = chatName;
    }

    if (
      JSON.stringify(instructionList) !==
      JSON.stringify(chat?.InstructionList ?? [])
    ) {
      payload.InstructionList = instructionList;
    }

    updateChat({
      data:
        type === "view"
          ? {
              type: "view",
              chatID: chatID,
              viewID: props.viewID,
              payload: payload,
            }
          : {
              type: "record",
              chatID: chatID,
              payload: payload,
              recordID: props.recordID!,
            },
      onSuccess: () => {
        handleClose();
        newToastEvent("success", "Chat updated successfully.");
      },
      onError: () => {
        setError("An unexpected error occurred. Please try again.");
      },
    });
  }
}
